import { atom } from 'jotai';
import { FieldPath } from 'react-hook-form';
import { FieldEntity } from '../../../data-models/field2.data-model';
import {
  createPanelPreferences,
  PanelPreferences,
  PreferenceKey,
  PreferenceScope,
} from '../../../data-models/preferences-v3.data-model';
import { DealFormViewModel } from '../../../schemas/deal.schema';
import { customFieldsByEntityLoadable } from '../../../services/state/AdminPanel/CustomFieldsStateJ';
import { preferenceAtom } from '../../../util/preferences-service-v3';
import { CompanyDataModel2 } from '../../../schemas/Company.schema';
import { ICompanyViewModel } from '../../../view-models/company.view-model';

export type MainDealPanelKeyType = keyof Omit<DealFormViewModel, 'customData'>;
export type DealAuditAndCustomFieldsKeyType = FieldPath<DealFormViewModel> | string;

// also use for data display config (if any fields are added, make sure deal vm schema has them)
export const availableDealFields: MainDealPanelKeyType[] = [
  'dealLeadId',
  'dealTeamIds',
  'roundId',
  'fundIds',
  'gcAmountMin',
  'gcAmountMax',
  'raiseAmount',
  'totalPic',
];

export const dealDrawerMainDealPanelPreferencesAtom = preferenceAtom(
  { preferenceKey: `${PreferenceKey.dealDrawerMainDealPanel}.default`, scope: PreferenceScope.ORGANIZATION },
  undefined,
  createPanelPreferences({
    selectedFields: availableDealFields,
    visible: true,
  }) as PanelPreferences<MainDealPanelKeyType>
);

const defaultDealAuditFields: (keyof DealFormViewModel)[] = ['labels'];
export const dealDrawerCustomFieldsPanelPreferencesAtom = preferenceAtom(
  {
    preferenceKey: `${PreferenceKey.dealDrawerDealCustomDataPanel}.default`,
    scope: PreferenceScope.ORGANIZATION,
  },
  undefined,
  createPanelPreferences({
    selectedFields: [],
    visible: true,
  }) as PanelPreferences<DealAuditAndCustomFieldsKeyType>
);

export const availableDealAuditAndCustomFieldsAtom = atom((get) => {
  const cfByEntityLoadable = get(customFieldsByEntityLoadable);
  if (cfByEntityLoadable.state !== 'hasData' || !cfByEntityLoadable.data) {
    return;
  }
  const customFields = cfByEntityLoadable.data.get(FieldEntity.deal)?.map((f) => f.entityField) ?? [];
  return [...defaultDealAuditFields, ...customFields] as DealAuditAndCustomFieldsKeyType[];
});

export type DealCompanyFieldsKeyType = FieldPath<ICompanyViewModel> | keyof CompanyDataModel2;
export const availableDealCompanyFields = [
  '_viewModel.primaryLocation',
  '_viewModel.ceoName',
  'sectorId',
  'website',
  '_viewModel.founderNames',
  'internalSourceId',
  'source',
  'sourceCompany',
  'sourceType',
  'foundedDate',
] as DealCompanyFieldsKeyType[];

export const dealDrawerCompanyFieldsPanelPreferencesAtom = preferenceAtom(
  { preferenceKey: `${PreferenceKey.dealDrawerCompanyPanel}.default`, scope: PreferenceScope.ORGANIZATION },
  undefined,
  createPanelPreferences({
    selectedFields: availableDealCompanyFields,
    visible: true,
  }) as PanelPreferences<DealCompanyFieldsKeyType>
);
